import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import CommandList from './CommandList';
import CommandModal from './CommandModal';
import CategoryNav from './CategoryNav';
import BuiltInVariables from './BuiltInVariables';
import './BAIQLCommandPanel.css';

// Lazy load command data
let commandsDataCache = null;
const loadCommandsData = async () => {
  if (commandsDataCache) return commandsDataCache;
  try {
    const response = await import('../../data/baiql-commands.json');
    commandsDataCache = response.default || response;
    return commandsDataCache;
  } catch (error) {
    console.error('Failed to load BAIQL commands data:', error);
    return { commands: [], categories: [], builtInVariables: [] };
  }
};

const BAIQLCommandPanel = ({ userTier = 'free', isScriptEditor = true }) => {
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCommand, setSelectedCommand] = useState(null);
  const [expandedCategories, setExpandedCategories] = useState(new Set());
  const [commandsData, setCommandsData] = useState(null);
  const [activeTab, setActiveTab] = useState('commands'); // 'commands' | 'variables'
  const searchInputRef = useRef(null);
  const panelRef = useRef(null);

  // Tier hierarchy for filtering
  const tierHierarchy = { free: 0, pro: 1, team: 2, enterprise: 3, admin: 4 };
  const normalizedTier = (userTier || 'free').toLowerCase();

  // Load commands data on first open
  useEffect(() => {
    if (isPanelOpen && !commandsData) {
      loadCommandsData().then(data => {
        setCommandsData(data);
        // Expand all categories by default
        if (data.categories) {
          setExpandedCategories(new Set(data.categories));
        }
      });
    }
  }, [isPanelOpen, commandsData]);

  // F1 keyboard shortcut - only toggles panel, doesn't interfere with typing
  useEffect(() => {
    if (!isScriptEditor) return;

    const handleKeyDown = (e) => {
      // Don't interfere if typing in modals
      const isModal = e.target.closest('.app-modal') !== null || 
                     e.target.closest('.baiql-command-modal-overlay') !== null;
      
      if (isModal) return; // Let modals handle their own keyboard events

      // F1 toggle panel (works everywhere, allows typing while panel is open)
      if (e.key === 'F1') {
        e.preventDefault();
        e.stopPropagation();
        setIsPanelOpen(prev => !prev);
        return;
      }

      // Ctrl+F / Cmd+F focus search when panel is open (but not in Monaco editor or inputs)
      const isMonacoEditor = e.target.closest('.monaco-editor') !== null;
      const isInput = e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA';
      
      if ((e.ctrlKey || e.metaKey) && e.key === 'f' && isPanelOpen && !isMonacoEditor && !isInput) {
        // Only if we're focused on the panel area
        if (panelRef.current?.contains(document.activeElement)) {
          e.preventDefault();
          searchInputRef.current?.focus();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown, true); // Use capture phase to catch early
    return () => window.removeEventListener('keydown', handleKeyDown, true);
  }, [isScriptEditor, isPanelOpen]);

  // Filter commands by tier
  const availableCommands = useMemo(() => {
    if (!commandsData?.commands) return [];
    const userTierLevel = tierHierarchy[normalizedTier] ?? 0;
    return commandsData.commands.filter(cmd => {
      const cmdTier = (cmd.tier || 'free').toLowerCase();
      const cmdTierLevel = tierHierarchy[cmdTier] ?? 0;
      return cmdTierLevel <= userTierLevel;
    });
  }, [commandsData, normalizedTier]);

  // Filter by search query
  const filteredCommands = useMemo(() => {
    let filtered = availableCommands;

    // Filter by category if selected
    if (selectedCategory) {
      filtered = filtered.filter(cmd => cmd.category === selectedCategory);
    }

    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(cmd =>
        cmd.name.toLowerCase().includes(query) ||
        cmd.syntax.toLowerCase().includes(query) ||
        cmd.quickDesc.toLowerCase().includes(query) ||
        (cmd.fullDocs && cmd.fullDocs.toLowerCase().includes(query))
      );
    }

    return filtered;
  }, [availableCommands, selectedCategory, searchQuery]);

  // Get locked commands (commands user can't access)
  const lockedCommands = useMemo(() => {
    if (!commandsData?.commands) return [];
    const userTierLevel = tierHierarchy[normalizedTier] ?? 0;
    return commandsData.commands.filter(cmd => {
      const cmdTier = (cmd.tier || 'free').toLowerCase();
      const cmdTierLevel = tierHierarchy[cmdTier] ?? 0;
      return cmdTierLevel > userTierLevel;
    });
  }, [commandsData, normalizedTier]);

  // Get command counts by category
  const categoryCounts = useMemo(() => {
    const counts = new Map();
    if (!commandsData?.categories) return counts;

    commandsData.categories.forEach(cat => {
      const count = availableCommands.filter(cmd => cmd.category === cat).length;
      counts.set(cat, count);
    });

    return counts;
  }, [commandsData, availableCommands]);

  // Handle category toggle
  const handleToggleCategory = useCallback((category) => {
    setExpandedCategories(prev => {
      const next = new Set(prev);
      if (next.has(category)) {
        next.delete(category);
      } else {
        next.add(category);
      }
      return next;
    });
  }, []);

  // Handle category click
  const handleCategoryClick = useCallback((category) => {
    setSelectedCategory(prev => prev === category ? null : category);
    setSearchQuery(''); // Clear search when selecting category
  }, []);

  // Handle command click
  const handleCommandClick = useCallback((command) => {
    setSelectedCommand(command);
  }, []);

  // Handle modal close
  const handleModalClose = useCallback(() => {
    setSelectedCommand(null);
  }, []);

  // Handle search change with debounce
  const handleSearchChange = useCallback((e) => {
    setSearchQuery(e.target.value);
    // Clear category selection when searching
    if (e.target.value.trim()) {
      setSelectedCategory(null);
    }
  }, []);

  // Clear search
  const handleClearSearch = useCallback(() => {
    setSearchQuery('');
    searchInputRef.current?.focus();
  }, []);

  if (!isScriptEditor) return null;

  return (
    <>
      {/* Main Panel - No overlay, stays open while typing */}
      <div
        ref={panelRef}
        className={`baiql-command-panel ${isPanelOpen ? 'open' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label="BAIQL Command Reference"
      >
        {/* Header */}
        <div className="baiql-command-panel-header">
          <h2>BAIQL Command Reference</h2>
          <button
            className="baiql-command-panel-close"
            onClick={() => setIsPanelOpen(false)}
            aria-label="Close panel"
          >
            ×
          </button>
        </div>

        {/* Tabs */}
        <div className="baiql-command-panel-tabs">
          <button
            className={activeTab === 'commands' ? 'active' : ''}
            onClick={() => setActiveTab('commands')}
          >
            Commands
          </button>
          <button
            className={activeTab === 'variables' ? 'active' : ''}
            onClick={() => setActiveTab('variables')}
          >
            Variables
          </button>
        </div>

        {/* Content */}
        <div className="baiql-command-panel-content">
          {activeTab === 'commands' ? (
            <>
              {/* Search */}
              <div className="baiql-command-panel-search">
                <input
                  ref={searchInputRef}
                  type="text"
                  placeholder="Search commands..."
                  value={searchQuery}
                  onChange={handleSearchChange}
                  className="baiql-command-panel-search-input"
                />
                {searchQuery && (
                  <button
                    className="baiql-command-panel-search-clear"
                    onClick={handleClearSearch}
                    aria-label="Clear search"
                  >
                    ×
                  </button>
                )}
              </div>

              {/* Category Navigation */}
              <CategoryNav
                categories={commandsData?.categories || []}
                categoryCounts={categoryCounts}
                selectedCategory={selectedCategory}
                expandedCategories={expandedCategories}
                onCategoryClick={handleCategoryClick}
                onToggleExpand={handleToggleCategory}
              />

              {/* Command List */}
              <CommandList
                commands={filteredCommands}
                lockedCommands={lockedCommands}
                searchQuery={searchQuery}
                userTier={normalizedTier}
                onCommandClick={handleCommandClick}
              />
            </>
          ) : (
            <>
              {/* Search for Variables tab */}
              <div className="baiql-command-panel-search">
                <input
                  type="text"
                  placeholder="Search variables..."
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                  }}
                  className="baiql-command-panel-search-input"
                />
                {searchQuery && (
                  <button
                    className="baiql-command-panel-search-clear"
                    onClick={handleClearSearch}
                    aria-label="Clear search"
                  >
                    ×
                  </button>
                )}
              </div>
              <BuiltInVariables
                variables={commandsData?.builtInVariables || []}
                searchQuery={searchQuery}
              />
            </>
          )}
        </div>
      </div>

      {/* Command Modal */}
      {selectedCommand && (
        <CommandModal
          command={selectedCommand}
          userTier={normalizedTier}
          isOpen={!!selectedCommand}
          onClose={handleModalClose}
        />
      )}
    </>
  );
};

export default BAIQLCommandPanel;

